import { motion } from 'framer-motion';
import { QTYPES } from '../../data/demoQuestions';
import { useDemoTheme } from './DemoThemeContext';

const STATUS_STYLES = {
  unvisited: { bg: 'var(--d-subtle)',  border: 'var(--d-border-sub)', text: 'var(--d-text-3)' },
  current:   { bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.55)', text: '#93c5fd' },
  answered:  { bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.4)', text: '#4ade80' },
  review:    { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.45)', text: '#fbbf24' },
  skipped:   { bg: 'var(--d-subtle)',  border: 'var(--d-border-sub)', text: 'var(--d-text-3)' },
};

const STATUS_DOT = {
  unvisited: { color: 'var(--d-text-3)', label: 'Unvisited' },
  current:   { color: '#3b82f6', label: 'Current' },
  answered:  { color: '#22c55e', label: 'Answered' },
  review:    { color: '#f59e0b', label: 'Review' },
  skipped:   { color: 'var(--d-text-3)', label: 'Skipped' },
};

const TYPE_BADGE = {
  [QTYPES.MCQ]:    { label: 'MCQ',  color: '#818cf8' },
  [QTYPES.CODING]: { label: 'Code', color: '#34d399' },
  [QTYPES.GRID]:   { label: 'Grid', color: '#fb923c' },
  [QTYPES.FILE]:   { label: 'File', color: '#a78bfa' },
};

export default function QuestionNavigator({ questions, currentQuestion, questionStatus, onNavigate, onSubmit }) {
  const { isDark } = useDemoTheme();
  const answered = Object.values(questionStatus).filter((s) => s === 'answered' || s === 'review').length;
  const reviewed = Object.values(questionStatus).filter((s) => s === 'review').length;

  return (
    <aside data-testid="question-navigator" className="h-full flex flex-col p-4">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-widest mb-0.5" style={{ color: 'var(--d-text-3)', letterSpacing: '0.12em' }}>Question Navigator</h3>
        <p className="text-xs" style={{ color: 'var(--d-text-3)' }}>{answered} of {questions.length} answered</p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-5 gap-1.5 mb-5">
        {questions.map((q, idx) => {
          const status = questionStatus[q.id] || 'unvisited';
          const s = STATUS_STYLES[status];
          const isCurrent = idx === currentQuestion;
          return (
            <motion.button key={q.id} data-testid={`nav-q-${q.id}`} onClick={() => onNavigate(idx)}
              whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.96 }}
              className="relative aspect-square rounded-lg flex items-center justify-center text-xs font-semibold font-mono transition-all duration-150"
              style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text, boxShadow: isCurrent ? `0 0 0 2px ${STATUS_STYLES.current.border}` : 'none' }}
              title={`Q${q.id} · ${q.category} · ${status}`}>
              {q.id}
              {status === 'review' && <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ background: '#f59e0b' }} />}
            </motion.button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="space-y-1.5 mb-5">
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--d-text-3)', letterSpacing: '0.12em' }}>Legend</p>
        {Object.entries(STATUS_DOT).map(([key, val]) => (
          <div key={key} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: val.color, opacity: key === 'skipped' ? 0.4 : 1 }} />
            <span className="text-xs capitalize" style={{ color: 'var(--d-text-2)' }}>
              {val.label}
              {key === 'review' && reviewed > 0 && <span className="ml-1" style={{ color: '#f59e0b' }}>({reviewed})</span>}
            </span>
          </div>
        ))}
      </div>

      {/* Type badges */}
      <div className="flex flex-wrap gap-1.5 mb-5 pb-5" style={{ borderBottom: '1px solid var(--d-border-sub)' }}>
        {Object.entries(TYPE_BADGE).map(([type, cfg]) => (
          <span key={type} className="text-xs px-2 py-0.5 rounded-md font-mono"
            style={{ background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
            {cfg.label}
          </span>
        ))}
      </div>

      {/* Question list */}
      <div className="flex-1 overflow-y-auto space-y-1 mb-4 min-h-0">
        {questions.map((q, idx) => {
          const status = questionStatus[q.id] || 'unvisited';
          const badge = TYPE_BADGE[q.type];
          const isCurrent = idx === currentQuestion;
          return (
            <button key={q.id} onClick={() => onNavigate(idx)}
              className="w-full text-left rounded-lg px-2.5 py-2 flex items-center gap-2.5 transition-colors"
              style={{
                background: isCurrent ? 'rgba(59,130,246,0.08)' : 'transparent',
                border: isCurrent ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent',
              }}
              onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = 'var(--d-hover)'; }}
              onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}>
              <span className="text-xs font-mono flex-shrink-0" style={{ color: 'var(--d-text-3)', minWidth: 20 }}>Q{q.id}</span>
              <span className="text-xs font-mono flex-shrink-0 px-1.5 py-0.5 rounded" style={{ background: `${badge.color}15`, color: badge.color, fontSize: 9 }}>{badge.label}</span>
              <span className="text-xs truncate flex-1" style={{ color: isCurrent ? 'var(--d-text-hi)' : 'var(--d-text-2)' }}>{q.title}</span>
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: STATUS_DOT[status]?.color || 'var(--d-text-3)' }} />
            </button>
          );
        })}
      </div>

      {/* Submit */}
      <button data-testid="navigator-submit-btn" onClick={onSubmit}
        className="w-full rounded-xl py-3 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', boxShadow: '0 4px 16px rgba(124,58,237,0.3)' }}>
        Submit Assessment
      </button>
    </aside>
  );
}
